import { useEffect, useRef } from 'react';
import { useEditorStore } from '../stores';
import type { EditorTool } from '../types';

const TOOL_SHORTCUTS: Record<string, EditorTool> = {
  v: 'select',
  h: 'pan',
  l: 'line',
  p: 'curve',
  r: 'rectangle',
  c: 'circle',
  g: 'polygon',
  t: 'text',
};

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    target.isContentEditable ||
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT'
  );
}

export function useEditorKeyboardShortcuts(enabled = true) {
  const setTool = useEditorStore((state) => state.setTool);
  const toolBeforeSpaceRef = useRef<EditorTool | null>(null);
  const spaceHeldRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;

      if (e.key === ' ' || e.code === 'Space') {
        if (e.repeat) return;
        e.preventDefault();
        const currentTool = useEditorStore.getState().currentTool;
        if (!spaceHeldRef.current) {
          spaceHeldRef.current = true;
          toolBeforeSpaceRef.current = currentTool;
          if (currentTool !== 'pan') {
            setTool('pan');
          }
        }
        return;
      }

      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.repeat) return;

      const tool = TOOL_SHORTCUTS[e.key.toLowerCase()];
      if (!tool) return;

      e.preventDefault();
      setTool(tool);
    };

    const releaseSpacePan = () => {
      if (!spaceHeldRef.current) return;

      spaceHeldRef.current = false;
      const previousTool = toolBeforeSpaceRef.current;
      toolBeforeSpaceRef.current = null;

      if (previousTool && previousTool !== 'pan') {
        setTool(previousTool);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key !== ' ' && e.code !== 'Space') return;
      releaseSpacePan();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        releaseSpacePan();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', releaseSpacePan);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', releaseSpacePan);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, setTool]);
}
